import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import { useLanguage } from 'context/languageContext'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

interface Dropdown {
  title: string
  options: {
    text: string
    lang: string
  }[]
  isMobile?: boolean
  toggleHandler?: () => void
}

export default function DropdownLang({
  title,
  options,
  isMobile,
  toggleHandler,
}: Dropdown) {
  const [isOpenMenu, setIsOpenMenu] = useState(false)
  const [isNavFixed, setisNavFixed] = useState(false)
  const { language, setLanguage } = useLanguage()

  const toggleDropdown = () => {
    setIsOpenMenu(!isOpenMenu)
  }
  const dropdownRef = useRef<any>()
  const solutionsRef = useRef<any>()

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !solutionsRef.current.contains(e.target)
      ) {
        setIsOpenMenu(false)
        // console.log('Out')
      }
    }

    document.addEventListener('click', handleOutsideClick)

    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [])

  const handleScroll = () => {
    const scrollPos = window.scrollY
    if (scrollPos > 80) {
      setisNavFixed(true)
      setIsOpenMenu(false)
    } else {
      setisNavFixed(false)
    }
  }

  const languages: { [key: string]: string } = useMemo(
    () => ({
      en: 'English',
      'zh-Hans': 'Chinese',
      fr: 'French',
      de: 'German',
      ja: 'Japanese',
      es: 'Spanish',
      th: 'Thai',
    }),
    [],
  )

  return (
    <div>
      {!isMobile ? (
        <button
          ref={solutionsRef}
          onClick={toggleDropdown}
          type="button"
          className="dropdown-toggle hover:fill-white fill-blue group border-1 border-blue text-blue hover:text-white hover:bg-blue !font-semibold capitalize flex justify-cengter gap-3 items-center text-base 
            py-2.5 px-5 rounded-full place-content-center  w-full hover:bg-opacity-90 transition-colors duration-300"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6"
            viewBox="0 0 24 24"
          >
            <path
              fill="inherit"
              // fill="#00BFFF"
              d="M11 1H3c-1.1 0-2 .9-2 2v12l3-3h5v-1c0-2.2 1.79-4 4-4V3c0-1.1-.9-2-2-2m0 3H9.5c-.34 1.19-.96 2.3-1.82 3.26l-.02.02l1.26 1.25l-.37 1.01L7 8l-2.5 2.5l-.69-.73l2.53-2.49A8.6 8.6 0 0 1 4.86 5h.99c.31.6.69 1.17 1.15 1.68A7.7 7.7 0 0 0 8.57 4H3V3h3.5V2h1v1H11zm10 5h-8c-1.1 0-2 .9-2 2v7c0 1.1.9 2 2 2h7l3 3V11c0-1.1-.9-2-2-2m-1.37 10l-.85-2.25h-3.56L14.38 19h-1.5l3.37-9h1.5l3.38 9zM17 12l1.22 3.25h-2.43z"
            />
          </svg>

          {language}
          {/* <span className="group-hover:text-white transition-colors duration-300">
          </span> */}
        </button>
      ) : (
        <button
          role="link"
          type="button"
          onClick={toggleDropdown}
          className={` bg-blue text-white flex justify-cengter gap-3 items-center text-lg font-normal border-2 border-transparent
            pt-2 pb-2 px-3 rounded-full place-content-center  w-full focus:bg-opacity-90 transition-colors duration-300 ${
              isOpenMenu && '!border-blueDark'
            }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5"
            viewBox="0 0 24 24"
          >
            <path
              fill="white"
              d="M11 1H3c-1.1 0-2 .9-2 2v12l3-3h5v-1c0-2.2 1.79-4 4-4V3c0-1.1-.9-2-2-2m0 3H9.5c-.34 1.19-.96 2.3-1.82 3.26l-.02.02l1.26 1.25l-.37 1.01L7 8l-2.5 2.5l-.69-.73l2.53-2.49A8.6 8.6 0 0 1 4.86 5h.99c.31.6.69 1.17 1.15 1.68A7.7 7.7 0 0 0 8.57 4H3V3h3.5V2h1v1H11zm10 5h-8c-1.1 0-2 .9-2 2v7c0 1.1.9 2 2 2h7l3 3V11c0-1.1-.9-2-2-2m-1.37 10l-.85-2.25h-3.56L14.38 19h-1.5l3.37-9h1.5l3.38 9zM17 12l1.22 3.25h-2.43z"
            />
          </svg>
          <span className="sg-translate">{languages[language]}</span>
        </button>
      )}

      <CSSTransition
        in={isOpenMenu}
        timeout={400}
        unmountOnExit
        onEnter={(node: any) =>
          gsap.fromTo(
            node,
            { opacity: 0, y: 10 },
            { opacity: 1, y: 0, duration: 0.3 },
          )
        }
        onExiting={(node: any) =>
          gsap.to(node, { opacity: 0, y: 10, duration: 0.3 })
        }
      >
        <div className={`dropdown ${isMobile && 'relative'}`} ref={dropdownRef}>
          <div
            className={`dropdown-content flex flex-col cursor-default gap-3 bg-white bg-footer 
            shadow bg-no-repeat bg-cover bg-right-bottom rounded-sm px-5 pt-4 pb-5 2xl:min-w-[350px] min-w-[250px] ${
              isMobile && '!absolute !-top-[17rem] !left-0'
            }`}
          >
            {options.map((item) => {
              return (
                <button
                  type="button"
                  className={`2xl:text-base text-sm hover:text-blue font-normal transition-all duration-300 relative block text-left ${
                    language === item.lang ? 'text-blue font-bold' : ''
                  }`}
                  key={item.lang}
                  onClick={() => {
                    // // TODO: Remove this later if header contact button navigation bug is fixed
                    // if (item.lang === 'en') {
                    //   window.location.reload()
                    // }
                    setLanguage(item.lang)
                    setIsOpenMenu(false)
                    toggleHandler?.()
                    localStorage.setItem('sg-lang-changed', 'true')
                  }}
                >
                  {item.text}
                </button>
              )
            })}
          </div>
        </div>
      </CSSTransition>
    </div>
  )
}
